/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import MobileDashboard from './components/MobileDashboard';
import TransactionForm from './components/TransactionForm';
import TransactionList from './components/TransactionList';
import MobileTransactionList from './components/MobileTransactionList';
import BudgetManager from './components/BudgetManager';
import SavingsGoals from './components/SavingsGoals';
import Settings from './components/Settings';
import LoanCalculator from './components/LoanCalculator';
import FinancialReports from './components/FinancialReports';
import ComingSoon from './components/ComingSoon';
import { ThemeProvider, useTheme as useCustomTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthModal from './components/AuthModal';
import { Category, Transaction, Budget, SavingsGoal } from './types';
import { useLocalStorage } from './hooks/useLocalStorage';
import { RealTimeSyncService } from './services/realTimeSyncService';
import './App.css';

// Le thème est maintenant géré par ThemeContext

const defaultCategories: Category[] = [
  { id: '1', name: 'Salaire', color: '#4caf50', type: 'income' },
  { id: '2', name: 'Freelance', color: '#8bc34a', type: 'income' },
  { id: '3', name: 'Logement', color: '#f44336', type: 'expense' },
  { id: '4', name: 'Alimentation', color: '#ff9800', type: 'expense' },
  { id: '5', name: 'Transport', color: '#2196f3', type: 'expense' },
  { id: '6', name: 'Loisirs', color: '#9c27b0', type: 'expense' },
  { id: '7', name: 'Santé', color: '#009688', type: 'expense' },
  { id: '8', name: 'Crédit', color: '#e91e63', type: 'expense' },
  { id: '9', name: 'Facture', color: '#2196f3', type: 'expense' },

];

function AppContent() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { currentUser } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(!currentUser);
  const [transactions, setTransactions] = useLocalStorage<Transaction[]>('transactions', []);
  const [categories] = useLocalStorage<Category[]>('categories', defaultCategories);
  const [budgets, setBudgets] = useLocalStorage<Budget[]>('budgets', []);
  const [savingsGoals, setSavingsGoals] = useLocalStorage<SavingsGoal[]>('savingsGoals', []);
  const [currentView, setCurrentView] = useState<'dashboard' | 'add-transaction' | 'transactions' | 'budgets' | 'accounts' | 'goals' | 'loan-calculator' | 'reports' | 'settings'>('dashboard');
  
  // Service de synchronisation temps réel
  const syncServiceRef = useRef<RealTimeSyncService | null>(null);

  // Initialisation et nettoyage du service de synchronisation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (currentUser) {
      console.log('🔄 Initialisation du service de synchronisation pour:', currentUser.email);
      
      // Créer le service de synchronisation
      syncServiceRef.current = new RealTimeSyncService(currentUser.uid);
      
      // Configurer la synchronisation temps réel (DÉSACTIVÉE temporairement pour debug)
      // syncServiceRef.current.setupRealTimeSync((cloudData: UserData) => {
      //   console.log('🔄 Mise à jour depuis le cloud reçue, transactions:', cloudData.transactions?.length || 0);
      //   // Ne mettre à jour que si on a des données différentes
      //   if (cloudData.transactions && cloudData.transactions.length !== transactions.length) {
      //     setTransactions(cloudData.transactions || []);
      //   }
      //   if (cloudData.budgets && cloudData.budgets.length !== budgets.length) {
      //     setBudgets(cloudData.budgets || []);
      //   }
      //   if (cloudData.savingsGoals && cloudData.savingsGoals.length !== savingsGoals.length) {
      //     setSavingsGoals(cloudData.savingsGoals || []);
      //   }
      // });

      // Charger les données au démarrage
      const loadInitialData = async () => {
        try {
          console.log('📡 Tentative de chargement depuis le cloud...');
          const cloudData = await syncServiceRef.current!.loadAllData();
          if (cloudData && cloudData.transactions) {
            console.log('📥 Données trouvées dans le cloud:', {
              transactions: cloudData.transactions.length,
              budgets: cloudData.budgets?.length || 0,
              savingsGoals: cloudData.savingsGoals?.length || 0
            });
            setTransactions(cloudData.transactions || []);
            setBudgets(cloudData.budgets || []);
            setSavingsGoals(cloudData.savingsGoals || []);
          } else {
            console.log('📤 Aucune donnée cloud, synchronisation des données locales');
            // Synchroniser les données locales vers le cloud si elles existent
            if (transactions.length > 0 || budgets.length > 0 || savingsGoals.length > 0) {
              await syncServiceRef.current!.syncAllData({
                transactions,
                budgets,
                savingsGoals
              });
            }
          }
        } catch (error) {
          console.error('❌ Erreur lors du chargement initial:', error);
        }
      };

      loadInitialData();

      return () => {
        // Nettoyer les listeners lors du démontage
        if (syncServiceRef.current) {
          syncServiceRef.current.cleanup();
        }
      };
    } else {
      // Nettoyer le service si l'utilisateur se déconnecte
      if (syncServiceRef.current) {
        syncServiceRef.current.cleanup();
        syncServiceRef.current = null;
      }
    }
  }, [currentUser]);

  // Synchroniser automatiquement lors des changements de données (DÉSACTIVÉ temporairement)
  // useEffect(() => {
  //   if (syncServiceRef.current && currentUser) {
  //     const syncChanges = async () => {
  //       try {
  //         await syncServiceRef.current!.syncAllData({
  //           transactions,
  //           budgets,
  //           savingsGoals
  //         });
  //       } catch (error) {
  //         console.error('❌ Erreur lors de la synchronisation automatique:', error);
  //       }
  //     };

  //     // Attendre un délai pour éviter les synchronisations trop fréquentes
  //     const timeoutId = setTimeout(syncChanges, 1000);
  //     return () => clearTimeout(timeoutId);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [transactions, budgets, savingsGoals, currentUser]);

  // Fonctions pour gérer les objectifs d'épargne
  const addSavingsGoal = (goal: Omit<SavingsGoal, 'id' | 'currentAmount'>) => {
    const newGoal: SavingsGoal = {
      ...goal,
      id: Date.now().toString(),
      currentAmount: 0,
    };
    setSavingsGoals([...savingsGoals, newGoal]);
  };

  const editSavingsGoal = (updatedGoal: SavingsGoal) => {
    setSavingsGoals(savingsGoals.map(g => 
      g.id === updatedGoal.id ? updatedGoal : g
    ));
  };

  const deleteSavingsGoal = (goalId: string) => {
    setSavingsGoals(savingsGoals.filter(g => g.id !== goalId));
  };

  const contributeToGoal = (goalId: string, amount: number) => {
    setSavingsGoals(savingsGoals.map(g => 
      g.id === goalId ? { ...g, currentAmount: g.currentAmount + amount } : g
    ));
  };

  const addTransaction = async (transaction: Omit<Transaction, 'id'>) => {
    // Validation simplifiée temporairement pour éviter les rejets
    console.log('💾 Ajout transaction:', transaction);
    
    const newTransaction: Transaction = {
      ...transaction,
      id: Date.now().toString(),
      date: new Date(transaction.date), // S'assurer que c'est un objet Date
    };
    
    const updatedTransactions = [...transactions, newTransaction];
    setTransactions(updatedTransactions);
    
    // Synchroniser manuellement seulement (plus d'auto-sync)
    if (syncServiceRef.current && currentUser) {
      try {
        console.log('💾 Sauvegarde manuelle de la transaction...');
        await syncServiceRef.current.syncAllData({
          transactions: updatedTransactions,
          budgets,
          savingsGoals
        });
        console.log('✅ Transaction sauvegardée dans le cloud');
      } catch (error) {
        console.error('❌ Erreur lors de la sauvegarde:', error);
        // Ne pas alerter pour éviter de perturber l'utilisateur
      }
    }
  };

  const editTransaction = (updatedTransaction: Transaction) => {
    setTransactions(transactions.map(t => 
      t.id === updatedTransaction.id ? updatedTransaction : t
    ));
  };

  const deleteTransaction = (transactionId: string) => {
    setTransactions(transactions.filter(t => t.id !== transactionId));
  };

  const addBudget = (budget: Omit<Budget, 'id' | 'spent' | 'remaining'>) => {
    const newBudget: Budget = {
      ...budget,
      id: Date.now().toString(),
      spent: 0,
      remaining: budget.allocated,
    };
    setBudgets([...budgets, newBudget]);
  };

  const editBudget = (updatedBudget: Budget) => {
    setBudgets(budgets.map(b => 
      b.id === updatedBudget.id ? updatedBudget : b
    ));
  };

  const deleteBudget = (budgetId: string) => {
    setBudgets(budgets.filter(b => b.id !== budgetId));
  };

  // Fonction pour gérer la synchronisation des données
  const handleDataSync = (data: {
    transactions: Transaction[];
    budgets: Budget[];
    savingsGoals: SavingsGoal[];
  }) => {
    setTransactions(data.transactions);
    setBudgets(data.budgets);
    setSavingsGoals(data.savingsGoals);
  };

  const renderCurrentView = () => {
    try {
      switch (currentView) {
        case 'dashboard':
          return isMobile 
            ? <MobileDashboard transactions={transactions} categories={categories} budgets={budgets} />
            : <Dashboard transactions={transactions} categories={categories} budgets={budgets} />;
        case 'add-transaction':
          return <TransactionForm categories={categories} transactions={transactions} onSubmit={addTransaction} />;
        case 'transactions':
          return isMobile 
            ? (
              <MobileTransactionList
                transactions={transactions}
                categories={categories}
                onEditTransaction={editTransaction}
                onDeleteTransaction={deleteTransaction}
              />
            )
            : (
              <TransactionList
                transactions={transactions}
                categories={categories}
                onEditTransaction={editTransaction}
                onDeleteTransaction={deleteTransaction}
              />
            );
        case 'budgets':
          return (
            <BudgetManager
              budgets={budgets}
              categories={categories}
              transactions={transactions}
              onAddBudget={addBudget}
              onEditBudget={editBudget}
              onDeleteBudget={deleteBudget}
            />
          );
        case 'accounts':
          return (
            <ComingSoon 
              title="Gestion des Comptes"
              description="Créez et gérez plusieurs comptes bancaires, cartes de crédit et portefeuilles. Suivez vos soldes en temps réel et organisez vos finances par compte."
              features={[
                "Comptes multiples",
                "Transferts entre comptes", 
                "Soldes en temps réel",
                "Historique détaillé",
                "Catégorisation avancée"
              ]}
              progress={25}
            />
          );
        case 'goals':
          return (
            <SavingsGoals
              goals={savingsGoals}
              onAddGoal={addSavingsGoal}
              onEditGoal={editSavingsGoal}
              onDeleteGoal={deleteSavingsGoal}
              onContribute={contributeToGoal}
            />
          );
        case 'loan-calculator':
          return <LoanCalculator transactions={transactions} />;
        case 'reports':
          return (
            <FinancialReports
              transactions={transactions}
              categories={categories}
            />
          );
        case 'settings':
          return (
            <Settings
              transactions={transactions}
              budgets={budgets}
              savingsGoals={savingsGoals}
              onDataSync={handleDataSync}
              syncService={syncServiceRef.current}
            />
          );
        default:
          return <Dashboard transactions={transactions} categories={categories} budgets={budgets} />;
      }
    } catch (error) {
      console.error('Erreur lors du rendu:', error);
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" color="error">
            Une erreur s'est produite lors du chargement de la page.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Vérifiez la console pour plus de détails.
          </Typography>
        </Box>
      );
    }
  };

  return (
    <>
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
      <ThemeProvider>
        <AppWithTheme 
          currentView={currentView}
          onViewChange={setCurrentView}
          renderCurrentView={renderCurrentView}
          isMobile={isMobile}
        />
      </ThemeProvider>
    </>
  );
}

function AppWithTheme({ currentView, onViewChange, renderCurrentView, isMobile }: {
  currentView: 'dashboard' | 'add-transaction' | 'transactions' | 'budgets' | 'accounts' | 'goals' | 'loan-calculator' | 'reports' | 'settings';
  onViewChange: (view: 'dashboard' | 'add-transaction' | 'transactions' | 'budgets' | 'accounts' | 'goals' | 'loan-calculator' | 'reports' | 'settings') => void;
  renderCurrentView: () => React.ReactNode;
  isMobile: boolean;
}) {
  const { isDarkMode } = useCustomTheme();

  return (
    <Box 
      className="App"
      data-theme={isDarkMode ? 'dark' : 'light'}
      sx={{ display: 'flex', minHeight: '100vh' }}
    >
      <Navigation currentView={currentView} onViewChange={onViewChange} />
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: isMobile ? 1 : 3,
          ...(isMobile && {
            marginTop: '64px', // Height of mobile AppBar
            marginBottom: '70px', // Height of bottom navigation
            minHeight: 'calc(100vh - 134px)', // Full height minus top and bottom bars
          }),
        }}
      >
        {renderCurrentView()}
      </Box>
    </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
